import { createTheme } from "@mui/material/styles";
import { swayyColors, swayyFont, swayyRadii } from "./tokens";

export const swayyTheme = createTheme({
  typography: {
    fontFamily: swayyFont,
    h1: { fontWeight: 700, letterSpacing: "-0.01em", color: swayyColors.textPrimary },
    h2: { fontWeight: 700, letterSpacing: "-0.01em", color: swayyColors.textPrimary },
    h3: { fontWeight: 700, letterSpacing: "-0.01em", color: swayyColors.textPrimary },
    h4: { fontWeight: 700, letterSpacing: "-0.01em", color: swayyColors.textPrimary },
    h5: { fontWeight: 600, letterSpacing: "-0.01em", color: swayyColors.textPrimary },
    h6: { fontWeight: 600, letterSpacing: "-0.01em", color: swayyColors.textPrimary },
    body1: { fontFamily: swayyFont, color: swayyColors.textPrimary },
    body2: { fontFamily: swayyFont, color: swayyColors.textSecondary },
    button: { fontFamily: swayyFont, fontWeight: 500, textTransform: "none" },
  },
  shape: {
    borderRadius: 10,
  },
  palette: {
    mode: "light",
    primary: {
      main: swayyColors.pine,
      dark: swayyColors.pineActive,
      light: swayyColors.pineHover,
      contrastText: swayyColors.white,
    },
    secondary: {
      main: swayyColors.deepGreen,
      light: swayyColors.deepGreenLight,
      contrastText: swayyColors.white,
    },
    background: {
      default: swayyColors.pageBg,
      paper: swayyColors.white,
    },
    text: {
      primary: swayyColors.textPrimary,
      secondary: swayyColors.textSecondary,
    },
    divider: swayyColors.border,
    success: { main: swayyColors.success },
    warning: { main: swayyColors.warning },
    error: { main: swayyColors.danger },
    info: { main: swayyColors.info },
    // Shared components (e.g. DateRangePickerNew) expect palette.customColors
    customColors: {
      white: swayyColors.white,
      blackBodyBackgorund: swayyColors.sidebarBg,
      greyBodyBackground: swayyColors.pageBg,
      lightGreyColor: swayyColors.pageBg,
      lightred: swayyColors.danger,
      darkBlueText: swayyColors.textPrimary,
      purpleMain: swayyColors.pine,
      lightOrange: "#FFE5C0",
      skyLight: swayyColors.pine,
      darkBlueGrey: swayyColors.sidebarBg,
      greyDarkborder: swayyColors.border,
      greyBorder: swayyColors.border,
      greyTextColor: swayyColors.textSecondary,
      greyBackground: swayyColors.pageBg,
      blackBorder: swayyColors.deepGreen,
      lightPurple: swayyColors.pineLight,
      backdrop: swayyColors.overlay,
      blueGradientStart: swayyColors.pine,
      blueGradientEnd: swayyColors.deepGreen,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: swayyFont,
          backgroundColor: swayyColors.pageBg,
          color: swayyColors.textPrimary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: swayyRadii.md,
          fontWeight: 500,
          fontFamily: swayyFont,
          padding: "10px 20px",
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          backgroundColor: swayyColors.pine,
          "&:hover": { backgroundColor: swayyColors.pineHover },
          "&:active": { backgroundColor: swayyColors.pineActive },
        },
        outlinedPrimary: {
          borderColor: swayyColors.border,
          color: swayyColors.textPrimary,
          "&:hover": {
            borderColor: swayyColors.pine,
            backgroundColor: swayyColors.pineLight,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: swayyRadii.lg,
          backgroundImage: "none",
        },
        outlined: {
          borderColor: swayyColors.border,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: swayyRadii.sm,
          fontFamily: swayyFont,
          backgroundColor: swayyColors.white,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: swayyColors.pine,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: swayyColors.pine,
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${swayyColors.pineLight}`,
          },
        },
        notchedOutline: {
          borderColor: swayyColors.border,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: swayyRadii.md,
          border: `1px solid ${swayyColors.border}`,
          boxShadow: "0 8px 24px rgba(0, 81, 84, 0.08)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: swayyFont,
          fontSize: 13,
          borderRadius: swayyRadii.sm,
          margin: "2px 6px",
          "&.Mui-selected": {
            backgroundColor: swayyColors.pineLight,
            color: swayyColors.deepGreen,
            "&:hover": { backgroundColor: swayyColors.pineLight },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: swayyFont,
          backgroundColor: swayyColors.deepGreen,
          fontSize: 12,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: swayyColors.pine,
        },
      },
    },
  },
});
